// Unassign the device behind a unique number — and make it a FRESH PIECE —
// from the Message Box's centre pane (both shells). The same action as Device
// Admin's, through the same core/devices/deviceAdmin.unassignDeviceFully, which
// now also runs core/devices/freshPiece.resetDeviceToFresh.
//
// ⚠️ IT RE-READS THE DEVICE FRESH BEFORE IT ASKS. The console's own row can be
// minutes old (and local-first on a phone); unassigning off it would name the
// wrong owner in the confirmation, or "unassign" a device somebody has since
// moved. `findDevice` is getDeviceDetailFresh — the server's answer now.
//
// ⚠️ PASSWORD-GUARDED, like Device Admin. Checked against the signed-in admin's
// session record: there is no "verify my password" endpoint, and this is a
// mis-click guard on a destructive write, not an authentication boundary — the
// console is already Super Admin only.
//
// An ALREADY-unassigned device is offered "Reset to fresh piece" on its own:
// devices unassigned before the reset existed still carry their old setup.
//
// The result is shown STEP BY STEP — a reset that half-ran must say which half.

import React, { useEffect, useRef, useState } from 'react';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, TextField,
  Alert, CircularProgress, Box,
} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useApp } from '../../core/state/AppContext';
import { findDevice, unassignDeviceFully } from '../../core/devices/deviceAdmin';
import { resetDeviceToFresh, freshDeviceName } from '../../core/devices/freshPiece';
import { FIT_XS } from './fitDialog';

const WILL_CLEAR = 'Button names and settings (All On / All Off, timers, power-on), schedules, '
  + 'two-way links, its steps in anyone’s scenes and wall buttons, the RF sensors that switch it, '
  + 'and any parked update. Its name goes back to ';

/** What the reset did, one line per step. */
function ResetReport({ reset }) {
  if (!reset) return null;
  return (
    <Box sx={{ mt: 1 }}>
      {reset.steps.map((s) => (
        <Box key={s.key} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
          {s.ok
            ? <CheckIcon sx={{ fontSize: 16, color: 'success.main', mt: '2px' }} />
            : <CloseIcon sx={{ fontSize: 16, color: 'error.main', mt: '2px' }} />}
          <Typography variant="body2" sx={{ fontSize: 13 }}>
            <b>{s.label}</b>
            {s.note ? ` — ${s.note}` : ''}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * @param uniqueNumber  the device
 * @param onDone        called after a successful unassign/reset (re-read the row)
 * @param sx            for the button
 * @param openSignal    with hideTrigger: a number that, when it changes (> 0),
 *                      opens the dialog — the device card's ⋮ menu item (the
 *                      menu unmounts its items, so the dialog cannot live there)
 * @param onClosedAfterDone  the result dialog was closed after a success — the
 *                      moment a room may drop the card (dropping it earlier
 *                      would unmount the report mid-read)
 * @param device        optional: the caller's current row, used ONLY for the
 *                      trigger's label — an unassigned device reads "Reset", not
 *                      a red "Unassign" that suggests it has an owner. The
 *                      dialog still re-reads fresh before it acts.
 */
export default function UnassignDeviceButton({
  uniqueNumber, onDone, sx, hideTrigger = false, openSignal = 0, onClosedAfterDone, device,
}) {
  const { user: me, isSuperAdmin, userId } = useApp();
  const uno = String(uniqueNumber || '').trim();
  const [open, setOpen] = useState(false);
  const [found, setFound] = useState(null);     // findDevice() result
  const [checking, setChecking] = useState(false);
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // { title, reset }

  const startRef = useRef(null);
  useEffect(() => {
    if (openSignal > 0 && startRef.current) startRef.current();
  }, [openSignal]);

  // The console is Super Admin only already; this is the in-component check the
  // route convention asks for, so the button cannot surface anywhere else bare.
  if (!isSuperAdmin || !uno) return null;

  const start = async () => {
    setChecking(true); setError(''); setPw(''); setResult(null);
    const r = await findDevice(uno).catch(() => null);
    setChecking(false);
    setFound(r);
    setOpen(true);
  };
  startRef.current = start;
  const close = () => {
    setOpen(false);
    if (result && onClosedAfterDone) onClosedAfterDone();
  };

  const run = async () => {
    if (!pw || String(pw) !== String(me?.Password ?? '')) {
      setError('That is not your password.');
      return;
    }
    setBusy(true); setError('');
    try {
      if (found.assigned) {
        const out = await unassignDeviceFully(found.device, uno, { userId });
        setResult({ title: `${uno} unassigned`, reset: out && out.reset });
      } else {
        const reset = await resetDeviceToFresh(uno, { device: found.device, userId });
        setResult({ title: `${uno} reset`, reset });
      }
      if (onDone) onDone();
    } catch (e) {
      setError(found.assigned
        ? 'Could not unassign. Nothing was changed — try again.'
        : 'Could not reset. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const d = found?.device;
  const ready = found?.found && !result;
  // Unknown (no row passed) keeps the old "Unassign" label.
  const rowLoc = device ? (device.LocationID ?? device.LocationId) : null;
  const knownUnassigned = rowLoc != null && Number(rowLoc) === 0;
  return (
    <>
      {/* Solid, not outlined: red TEXT on a coloured panel is unreadable on
          several company palettes; white on red reads on all of them. */}
      {!hideTrigger && (
      <Button
        size="small" variant="contained" color={knownUnassigned ? 'warning' : 'error'} sx={sx}
        startIcon={checking ? <CircularProgress size={12} />
          : (knownUnassigned ? <RestartAltIcon sx={{ fontSize: 14 }} /> : <LinkOffIcon sx={{ fontSize: 14 }} />)}
        disabled={checking}
        onClick={start}
      >
        {knownUnassigned ? 'Reset to fresh' : 'Unassign'}
      </Button>
      )}

      <Dialog open={open} onClose={busy ? undefined : close} {...FIT_XS}>
        <DialogTitle sx={{ pb: 0.5 }}>
          {/* eslint-disable-next-line no-nested-ternary */}
          {result ? result.title : (found?.assigned ? 'Unassign this device?' : 'Reset to a fresh piece?')}
        </DialogTitle>
        <DialogContent>
          {!found?.found && (
            <Alert severity="warning">{`No device ${uno} on the server.`}</Alert>
          )}

          {ready && (
            <>
              {/* What will be cleared, by name — "are you sure" tells the
                  operator nothing they can weigh. */}
              {found.assigned ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {`${d?.DeviceName || uno} (${uno}) will be detached from `}
                  <b>{d?.DeviceMasterUser || d?.DeviceMasterUserName || 'its owner'}</b>
                  {`, ${d?.LocationName || 'its location'}`}
                  {d?.RoomName ? ` / ${d.RoomName}` : ''}
                  {' and from every user, and disappears from their apps.'}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {`${d?.DeviceName || uno} (${uno}) is already unassigned, but may still carry its last customer’s setup.`}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                <b>Fresh piece:</b>
                {` ${WILL_CLEAR}`}
                <b>{freshDeviceName(uno)}</b>
                {'. Kept: its device type and firmware file.'}
              </Typography>
              {error && <Alert severity="error" sx={{ mb: 1, py: 0 }}>{error}</Alert>}
              <TextField
                size="small" fullWidth type="password" autoFocus
                label="Your password" value={pw}
                onChange={(e) => { setPw(e.target.value); setError(''); }}
                onKeyDown={(e) => e.key === 'Enter' && run()}
              />
            </>
          )}

          {result && (
            <>
              <Alert severity={result.reset && !result.reset.ok ? 'warning' : 'success'} sx={{ mb: 0.5 }}>
                {result.reset && !result.reset.ok
                  ? 'Done, but some parts could not be cleared — see below.'
                  : 'Done — this is now a fresh piece.'}
              </Alert>
              <ResetReport reset={result.reset} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          {result ? (
            <Button variant="contained" onClick={close}>Close</Button>
          ) : (
            <>
              <Button onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
              {found?.found && (
                <Button color="error" variant="contained" onClick={run} disabled={busy || !pw}>
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {busy ? <CircularProgress size={18} /> : (found.assigned ? 'Unassign' : 'Reset')}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
